import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { formatINR } from "@/lib/useReveal";

export const CartDrawer = () => {
  const {
    items,
    drawerOpen,
    setDrawerOpen,
    updateQty,
    removeItem,
    subtotal,
    mrpTotal,
    setCheckoutOpen,
  } = useCart();

  const savings = mrpTotal - subtotal;

  return (
    <>
      {drawerOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/50"
          onClick={() => setDrawerOpen(false)}
          data-testid="cart-overlay"
        />
      )}
      <aside
        data-testid="cart-drawer"
        className={`fixed right-0 top-0 z-[100] flex h-full w-full max-w-md flex-col bg-white shadow-2xl transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 className="flex items-center gap-2 font-display text-lg text-neutral-900">
            <ShoppingBag className="h-5 w-5 text-[var(--hp-crimson)]" /> Your Cart
          </h2>
          <button
            data-testid="cart-close-button"
            onClick={() => setDrawerOpen(false)}
            className="rounded-full p-2 hover:bg-neutral-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-neutral-500">
              <ShoppingBag className="h-12 w-12 text-neutral-300" />
              <p className="mt-3">Your cart is currently empty.</p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((it) => (
                <li
                  key={it.pack_id}
                  data-testid={`cart-item-${it.pack_id}`}
                  className="flex gap-3 rounded-xl border border-neutral-200 p-3"
                >
                  <div className="flex-1">
                    <div className="font-semibold text-neutral-900">{it.title}</div>
                    <div className="font-num text-lg font-bold text-[var(--hp-crimson)]">
                      {formatINR(it.price)}{" "}
                      <span className="font-num text-sm text-neutral-400 line-through">
                        {formatINR(it.mrp)}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex items-center rounded-lg border border-neutral-200">
                        <button
                          data-testid={`cart-dec-${it.pack_id}`}
                          onClick={() => updateQty(it.pack_id, -1)}
                          className="p-1.5 hover:bg-neutral-100"
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="w-8 text-center text-sm font-bold">{it.qty}</span>
                        <button
                          data-testid={`cart-inc-${it.pack_id}`}
                          onClick={() => updateQty(it.pack_id, 1)}
                          className="p-1.5 hover:bg-neutral-100"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.pack_id}`}
                        onClick={() => removeItem(it.pack_id)}
                        className="text-neutral-400 hover:text-[var(--hp-crimson)]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-neutral-200 px-5 py-4">
            {savings > 0 && (
              <div className="mb-2 rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm font-semibold text-emerald-700">
                You're saving {formatINR(savings)}! 🎉
              </div>
            )}
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm text-neutral-600">Total (incl. all taxes)</span>
              <span className="font-num text-2xl font-bold text-neutral-900">
                {formatINR(subtotal)}
              </span>
            </div>
            <Button
              data-testid="proceed-to-checkout-button"
              onClick={() => {
                setDrawerOpen(false);
                setCheckoutOpen(true);
              }}
              className="h-13 w-full bg-gradient-to-r from-red-600 to-rose-700 py-4 text-base font-bold text-white hover:from-red-700 hover:to-rose-800"
            >
              Proceed to Checkout →
            </Button>
          </div>
        )}
      </aside>
    </>
  );
};
